/**
 * Permission synchronization hook
 * Handles assignment permissions and email preferences synchronization
 */

import { useState, useCallback } from 'react';
import { invokeEdgeFunction } from '@/lib/supabase-functions-wrapper';
import { componentLogger } from '@/lib/logging';
import { AssignmentPermission } from '../UserDialogTypes';

export const usePermissionSync = () => {
  const [syncingPermissions, setSyncingPermissions] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  // Assignment permission sync function
  const syncAssignmentPermissions = useCallback(async (
    userId: string,
    newPermissions: AssignmentPermission[],
    oldPermissions: AssignmentPermission[]
  ): Promise<string[]> => {
    const errors: string[] = [];
    setSyncingPermissions(true);

    try {
      // Calculate permission changes
      const toRemove = oldPermissions.filter(old => !newPermissions.find(np => np.assignmentId === old.assignmentId));
      const toAdd = newPermissions.filter(np => !oldPermissions.find(old => old.assignmentId === np.assignmentId));
      const toUpdate = newPermissions.filter(np => {
        const old = oldPermissions.find(old => old.assignmentId === np.assignmentId);
        return old && old.permissionLevel !== np.permissionLevel;
      });

      const totalOperations = toRemove.length + toAdd.length + toUpdate.length;
      let completed = 0;

      // Remove permissions
      for (const permission of toRemove) {
        setSyncProgress(`Removing permission ${permission.assignmentName}... (${++completed}/${totalOperations})`);
        try {
          componentLogger.debug('usePermissionSync', 'Revoking permission', {
            userId,
            projectId: permission.assignmentId,
            permissionName: permission.assignmentName
          });

          const { data, error } = await invokeEdgeFunction('revoke-project-permission', {
            body: {
              projectId: permission.assignmentId,
              userId: userId,
            },
          });

          // Check both top-level error and response data error
          if (error) {
            throw error;
          }

          if (data && typeof data === 'object' && (data as { error?: string }).error) {
            throw new Error((data as { error: string }).error);
          }

          componentLogger.debug('usePermissionSync', 'Permission revoked successfully', {
            userId,
            projectId: permission.assignmentId
          });
        } catch (error) {
          errors.push(`Failed to remove ${permission.assignmentName}: ${(error as Error).message}`);
        }
      }

      // Add new permissions
      for (const permission of toAdd) {
        setSyncProgress(`Adding permission ${permission.assignmentName}... (${++completed}/${totalOperations})`);
        try {
          componentLogger.debug('usePermissionSync', 'Granting permission', {
            userId,
            projectId: permission.assignmentId,
            permissionLevel: permission.permissionLevel,
            permissionName: permission.assignmentName
          });

          const { data, error } = await invokeEdgeFunction('grant-project-permission', {
            body: {
              projectId: permission.assignmentId,
              userId: userId,
              permissionLevel: permission.permissionLevel,
            },
          });

          // Check both top-level error and response data error
          if (error) {
            throw error;
          }

          if (data && typeof data === 'object' && (data as { error?: string }).error) {
            throw new Error((data as { error: string }).error);
          }

          componentLogger.debug('usePermissionSync', 'Permission granted successfully', {
            userId,
            projectId: permission.assignmentId,
            permissionLevel: permission.permissionLevel
          });
        } catch (error) {
          errors.push(`Failed to add ${permission.assignmentName}: ${(error as Error).message}`);
        }
      }

      // Update existing permissions
      for (const permission of toUpdate) {
        setSyncProgress(`Updating permission ${permission.assignmentName}... (${++completed}/${totalOperations})`);
        try {
          componentLogger.debug('usePermissionSync', 'Updating permission', {
            userId,
            projectId: permission.assignmentId,
            permissionLevel: permission.permissionLevel,
            permissionName: permission.assignmentName
          });

          const { data, error } = await invokeEdgeFunction('grant-project-permission', {
            body: {
              projectId: permission.assignmentId,
              userId: userId,
              permissionLevel: permission.permissionLevel,
            },
          });

          // Check both top-level error and response data error
          if (error) {
            throw error;
          }

          if (data && typeof data === 'object' && (data as { error?: string }).error) {
            throw new Error((data as { error: string }).error);
          }

          componentLogger.debug('usePermissionSync', 'Permission updated successfully', {
            userId,
            projectId: permission.assignmentId,
            permissionLevel: permission.permissionLevel
          });
        } catch (error) {
          errors.push(`Failed to update ${permission.assignmentName}: ${(error as Error).message}`);
        }
      }
    } catch (error) {
      errors.push(`General sync error: ${(error as Error).message}`);
    } finally {
      setSyncingPermissions(false);
      setSyncProgress('');
    }

    return errors;
  }, []);

  // Email preferences sync function
  const syncEmailPreferences = useCallback(async (
    userId: string,
    assignmentPermissions: AssignmentPermission[],
    originalEmailPreferences: Record<string, boolean>,
    forceUpdate = false
  ) => {
    try {
      for (const permission of assignmentPermissions) {
        const originalPref = originalEmailPreferences[permission.assignmentId];
        const currentPref = permission.emailNotifications;

        // Sync if: force update, OR new permission with notifications enabled, OR existing permission changed
        const shouldSync = forceUpdate ||
                          (originalPref === undefined && currentPref === true) ||
                          (originalPref !== undefined && originalPref !== currentPref);

        if (shouldSync) {
          componentLogger.debug('usePermissionSync', 'Updating email preferences', {
            userId,
            assignmentId: permission.assignmentId,
            emailOnComments: currentPref
          });

          const { data, error } = await invokeEdgeFunction('update-email-preferences', {
            body: {
              assignmentId: permission.assignmentId,
              emailOnComments: currentPref,
              userId: userId,
            },
          });

          // Check both top-level error and response data error
          if (error) {
            throw error;
          }

          if (data && typeof data === 'object' && (data as { error?: string }).error) {
            throw new Error(`Failed to update email preference: ${(data as { error: string }).error}`);
          }

          componentLogger.debug('usePermissionSync', 'Email preferences updated successfully', {
            userId,
            assignmentId: permission.assignmentId,
            emailOnComments: currentPref
          });
        }
      }
    } catch (error) {
      componentLogger.error('usePermissionSync', 'Failed to sync email preferences', error);
      throw error;
    }
  }, []);

  return {
    syncingPermissions,
    syncProgress,
    setSyncingPermissions,
    setSyncProgress,
    syncAssignmentPermissions,
    syncEmailPreferences,
  };
};